"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState, useRef, useMemo } from "react";
import { useAuth } from "@/lib/contexts/AuthContext";
import { useSchool } from "@/lib/contexts/SchoolContext";
import { useDisplayName } from "@/lib/contexts/UserProfileContext";
import {
  Settings,
  School as SchoolIcon,
  ChevronDown,
  Check,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { HubPickerModal } from "@/components/HubPickerModal";
import { PRODUCTS, PRODUCTS_LIST, type ProductId } from "@/lib/constants/products";
import { isSubscriptionExpired, getPreviousHubs, isTrialEligible } from "@/lib/utils/subscriptionUtils";
import { APP_THEMES, type AppThemeKey } from "@/lib/constants/themes";
import { Copyright } from "@/components/Copyright";
import { AppGrid, APP_BG_COLORS } from "@/components/home/AppGrid";
import { ExpiredBanner } from "@/components/ExpiredBanner";
import { UnusedSeatsBanner } from "@/components/UnusedSeatsBanner";
import { QuickLinkDialog } from "@/components/home/QuickLinkDialog";
import { SchoolSetupForm } from "@/components/home/SchoolSetupForm";
import { AccountMenu } from "@/components/home/AccountMenu";
import { MENU_PANEL, MENU_HEADER, MENU_LABEL, MENU_ITEM, MENU_ITEM_SELECTED, MENU_ITEM_CHECK, MENU_SECTION } from '@/components/ui/menu-styles';

export default function RootPage() {
  const router = useRouter();
  const { user, session, isLoading: authLoading, signOut, needsPasswordChange } = useAuth();
  const {
    schools,
    activeSchool,
    activeSchoolLogoUrl,
    hasProductAccess,
    getProductRole,
    isOwner,
    isAdmin,
    isLoading: schoolLoading,
    switchSchool,
    updateSchoolName,
    updateSchoolState,
    createSchool,
  } = useSchool();
  const displayName = useDisplayName();
  const queryClient = useQueryClient();
  const [schoolDropdownOpen, setSchoolDropdownOpen] = useState(false);
  const schoolDropdownRef = useRef<HTMLDivElement>(null);
  const [hubPickerOpen, setHubPickerOpen] = useState(false);
  const [hubPickerPreSelected, setHubPickerPreSelected] = useState<ProductId | undefined>();
  const [hubPickerOverrides, setHubPickerOverrides] = useState<Record<string, number> | undefined>();
  const [addHubTarget, setAddHubTarget] = useState<{ id: string; name: string; href: string } | null>(null);
  const [hoveredApp, setHoveredApp] = useState<string | null>(null);
  const [cycleIndex, setCycleIndex] = useState(0);
  const [hasReferral, setHasReferral] = useState(false);
  const [referrerName, setReferrerName] = useState<string | null>(null);

  // Inline school setup for new users
  const [schoolNames, setSchoolNames] = useState<string[]>(['']);
  const [setupState, setSetupState] = useState('');
  const [schoolSetupSubmitting, setSchoolSetupSubmitting] = useState(false);
  const [schoolSetupError, setSchoolSetupError] = useState('');

  useEffect(() => {
    if (localStorage.getItem('referral_code')) {
      setHasReferral(true);
      setReferrerName(localStorage.getItem('referrer_name'));
    }
  }, []);

  // Cycle through app colors when nothing is hovered — one crossfade layer per
  // app that can appear on the home grid (all non-hidden hubs).
  const appIds = useMemo(() => PRODUCTS_LIST.filter(p => p.status !== 'hidden').map(p => p.id), []);
  useEffect(() => {
    if (hoveredApp) return;
    const interval = setInterval(() => {
      setCycleIndex(i => (i + 1) % appIds.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [hoveredApp, appIds]);

  const activeAppId = hoveredApp || appIds[cycleIndex];

  // Fetch subscriptions to detect unified sub for edit mode
  const { data: subData } = useQuery<{ subscriptions: { id: string; product_id: string; status: string; max_schools: number; stripe_subscription_id: string | null; interval: string | null; linked_schools?: { id: string }[]; trial_ends_at?: string | null; expires_at?: string | null }[]; freeTrialUsedAt?: string | null }>({
    queryKey: ['userSubscriptions'],
    queryFn: async () => {
      if (!session?.access_token) return { subscriptions: [] }
      const res = await fetch('/api/subscriptions', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (!res.ok) return { subscriptions: [] }
      return res.json()
    },
    enabled: !!session?.access_token,
  })

  // Build edit mode config if user has a unified subscription
  const editModeConfig = (() => {
    const subs = subData?.subscriptions || []
    // Include past_due: the Stripe sub is still live (just unpaid), so adding a hub must
    // edit it — routing these users to a fresh checkout would create a duplicate subscription.
    const activeSubs = subs.filter(s => ['active', 'trialing', 'cancelling', 'past_due'].includes(s.status) && s.stripe_subscription_id)
    if (activeSubs.length === 0) return null
    const currentHubs: Record<string, number> = {}
    for (const sub of activeSubs) {
      currentHubs[sub.product_id] = sub.max_schools
    }
    const interval: 'monthly' | 'annual' = activeSubs[0].interval === 'year' ? 'annual' : 'monthly'
    const isTrialing = activeSubs[0].status === 'trialing'
    const trialEndsAt = isTrialing ? (activeSubs[0].trial_ends_at ?? null) : null
    return { currentHubs, interval, isTrialing, trialEndsAt }
  })();

  // Detect trial state for badge display
  const trialProducts = new Set(
    (subData?.subscriptions || [])
      .filter(s => s.status === 'trialing')
      .map(s => s.product_id)
  );
  const isAnyTrialing = trialProducts.size > 0;

  // Detect expired products: status='expired' OR active/cancelling/past_due past expires_at,
  // OR trialing past trial_ends_at. Mirrors the helper used server-side.
  const expiredProducts = new Set(
    (subData?.subscriptions || [])
      .filter(s => isSubscriptionExpired({
        status: s.status,
        expires_at: s.expires_at ?? null,
        trial_ends_at: s.trial_ends_at ?? null,
      }))
      .map(s => s.product_id)
  );

  // Hubs the user previously had (cancelled/expired) — pre-fill the picker on resubscribe.
  // Only applies when they have no active sub (editModeConfig === null).
  const previousHubs = getPreviousHubs(subData?.subscriptions || [], id => PRODUCTS[id as ProductId]?.status === 'live');
  const previousHubIds = Object.keys(previousHubs) as ProductId[];
  // Free trial is once per account and only for brand-new accounts — a user with any
  // subscription history (including cancelled/expired/past_due) resubscribes without one.
  const trialEligible = isTrialEligible(
    subData?.freeTrialUsedAt,
    (subData?.subscriptions?.length ?? 0) > 0,
  );

  useEffect(() => {
    if (!authLoading && !user) {
      router.push("/login");
    }
    if (!authLoading && user && needsPasswordChange) {
      router.push("/accept-invite");
    }
  }, [user, authLoading, needsPasswordChange, router]);

  // Close school dropdown on click outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (schoolDropdownRef.current && !schoolDropdownRef.current.contains(e.target as Node)) {
        setSchoolDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (authLoading || schoolLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const isPlatformAdmin = user?.user_metadata?.role === 'platform_admin';
  const isBetaTester = user?.user_metadata?.is_beta_tester === true;
  // Derived from the registry, not hand-listed. The hand-written version silently omitted `a4u`,
  // so the store's tile fell back to the `isAdmin || status === 'beta'` escape in AppGrid and
  // `hasAnyAccess` could never count it. Any hub added to PRODUCTS is now included by construction.
  const accessMap: Record<string, boolean> = Object.fromEntries(
    PRODUCTS_LIST.map((p) => [p.id, hasProductAccess(p.id)])
  );
  const hasAnyAccess = Object.values(accessMap).some(Boolean);

  // New user onboarding: active school exists but has no name yet
  const needsSchoolSetup = isOwner && activeSchool && !activeSchool.name;


  const handleSchoolSetup = async () => {
    if (!schoolNames[0].trim() || !activeSchool) return;
    setSchoolSetupSubmitting(true);
    setSchoolSetupError('');

    try {
      await updateSchoolName(activeSchool.id, schoolNames[0].trim());
      if (setupState) await updateSchoolState(activeSchool.id, setupState);
      for (let i = 1; i < schoolNames.length; i++) {
        const n = schoolNames[i].trim();
        if (n) await createSchool(n, setupState || null);
      }
      queryClient.invalidateQueries({ queryKey: ['schools'] });
    } catch (err) {
      setSchoolSetupError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSchoolSetupSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 relative overflow-hidden">
      <HubPickerModal
        key={hubPickerOverrides ? JSON.stringify(hubPickerOverrides) : 'default'}
        isOpen={hubPickerOpen}
        onClose={() => { setHubPickerOpen(false); setHubPickerOverrides(undefined) }}
        preSelectedHub={hubPickerPreSelected}
        trialEligible={trialEligible}
        {...(editModeConfig ? {
          editMode: hubPickerOverrides ? {
            ...editModeConfig,
            currentHubs: { ...editModeConfig.currentHubs, ...hubPickerOverrides },
            originalHubs: editModeConfig.currentHubs,
          } : editModeConfig
        } : {
          // No active sub: pre-fill previously-owned hubs for one-step resubscribe
          defaultSelectedHubs: previousHubIds,
          defaultHubSchools: previousHubs,
        })}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['schools'] })
          queryClient.invalidateQueries({ queryKey: ['userSubscriptions'] })
          queryClient.invalidateQueries({ queryKey: ['productSubscriptions'] })
        }}
      />

      {/* Quick Link School Dialog */}
      <QuickLinkDialog
        addHubTarget={addHubTarget}
        setAddHubTarget={setAddHubTarget}
        subData={subData}
      />

      {/* Background layers — one per app, crossfade via opacity */}
      {[...appIds, 'settings'].map((id) => (
        <div
          key={id}
          className="absolute inset-0 pointer-events-none transition-opacity duration-1000 ease-in-out"
          style={{
            background: `linear-gradient(to bottom right, ${APP_BG_COLORS[id as AppThemeKey]})`,
            opacity: activeAppId === id ? 1 : 0,
          }}
        />
      ))}
      {/* Decorative blobs from active theme */}
      {(() => {
        const theme = APP_THEMES[activeAppId as AppThemeKey];
        return theme ? (
          <>
            <div className={cn("absolute -top-20 -right-20 w-96 h-96 rounded-full blur-3xl pointer-events-none transition-all duration-1000", theme.blob1)} />
            <div className={cn("absolute -bottom-20 -left-20 w-96 h-96 rounded-full blur-3xl pointer-events-none transition-all duration-1000", theme.blob2)} />
          </>
        ) : null;
      })()}

      {/* Expired subscription banner — surfaces silent failures */}
      <div className="relative z-50">
        <ExpiredBanner />
        <UnusedSeatsBanner />
      </div>

      {/* Top Nav Bar */}
      <div className="relative z-50 flex items-center gap-3 px-4 md:px-8 py-4">
        {/* School brand switcher */}
        {activeSchool && (
          <div ref={schoolDropdownRef} className="relative min-w-0 flex-1">
            <button
              onClick={() => schools.length > 1 && setSchoolDropdownOpen(!schoolDropdownOpen)}
              className={cn(
                "flex items-center gap-2.5",
                schools.length > 1 && "cursor-pointer hover:opacity-80 transition-opacity"
              )}
            >
              <img
                src={activeSchoolLogoUrl || '/a4uLogo.png'}
                alt={`${activeSchool.name || 'School'} Logo`}
                className="h-10 w-10 rounded-xl object-cover"
              />
              <span
                className="text-xl font-semibold text-gray-800 break-words"
                style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}
              >
                {activeSchool.name || 'My School'}
              </span>
              {schools.length > 1 && (
                <ChevronDown className={cn("h-4 w-4 shrink-0 text-gray-400 transition-transform", schoolDropdownOpen && "rotate-180")} />
              )}
            </button>
            {schoolDropdownOpen && (
              <div data-overlay className={cn(MENU_PANEL, "absolute left-0 top-full mt-2 w-60 z-50 max-h-72 overflow-y-auto")}>
                <div className={MENU_HEADER}><span className={MENU_LABEL}>Schools</span></div>
                {schools.map(school => {
                  const logoUrl = school.logo_path
                    ? `${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/school-logos/${school.logo_path}`
                    : null;
                  return (
                    <button
                      key={school.id}
                      onClick={() => {
                        switchSchool(school.id);
                        setSchoolDropdownOpen(false);
                      }}
                      disabled={school.id === activeSchool?.id}
                      className={cn(MENU_ITEM, school.id === activeSchool?.id && MENU_ITEM_SELECTED)}
                    >
                      {logoUrl ? (
                        <img src={logoUrl} alt="" className="h-8 w-8 rounded-lg object-cover shrink-0" />
                      ) : (
                        <SchoolIcon className="h-8 w-8 shrink-0 p-1.5 text-gray-400" />
                      )}
                      <span className="truncate flex-1 font-medium">{school.name || 'Unnamed School'}</span>
                      {school.id === activeSchool?.id && (
                        <Check className={MENU_ITEM_CHECK} />
                      )}
                    </button>
                  );
                })}
                <div className={MENU_SECTION}>
                  <Link
                    href="/settings/schools"
                    onClick={() => setSchoolDropdownOpen(false)}
                    className={cn(MENU_ITEM, "text-gray-500")}
                  >
                    <Settings className="h-8 w-8 shrink-0 p-1.5" />
                    <span className="font-medium">Manage Schools</span>
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
        {!activeSchool && (
          <div className="flex items-center gap-2.5">
            <img src="/hub-icons/settings-image.webp" alt="Admin4You Logo" className="h-10 w-10 rounded-xl object-cover" />
            <span className="text-xl font-semibold text-gray-800" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>
              Admin4You
            </span>
          </div>
        )}
        <div className="flex items-center gap-2 ml-auto shrink-0">
          <AccountMenu
            displayName={displayName}
            email={user?.email}
            isOwner={isOwner}
            isAdmin={isAdmin}
            isPlatformAdmin={isPlatformAdmin}
            onSignOut={async () => {
              await signOut();
              router.push("/login");
            }}
          />
        </div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 py-6 md:py-14 flex-1">
        {/* New user onboarding: welcome letter + school setup */}
        {needsSchoolSetup ? (
          <SchoolSetupForm
            displayName={displayName}
            schoolNames={schoolNames}
            setSchoolNames={setSchoolNames}
            selectedState={setupState}
            setSelectedState={setSetupState}
            schoolSetupError={schoolSetupError}
            schoolSetupSubmitting={schoolSetupSubmitting}
            onSubmit={handleSchoolSetup}
            hasReferral={hasReferral}
            referrerName={referrerName}
          />
        ) : (
          <AppGrid
            displayName={displayName}
            isOwner={isOwner}
            isAdmin={isAdmin}
            isPlatformAdmin={isPlatformAdmin}
            isBetaTester={isBetaTester}
            accessMap={accessMap}
            documentsRole={getProductRole('documents')}
            evalTeacherRole={getProductRole('eval_teacher')}
            hasAnyAccess={hasAnyAccess}
            activeSchool={activeSchool}
            trialProducts={trialProducts}
            isAnyTrialing={isAnyTrialing}
            expiredProducts={expiredProducts}
            editModeConfig={editModeConfig}
            subData={subData}
            hasReferral={hasReferral}
            referrerName={referrerName}
            hoveredApp={hoveredApp}
            setHoveredApp={setHoveredApp}
            setHubPickerOpen={setHubPickerOpen}
            setHubPickerPreSelected={setHubPickerPreSelected}
            setHubPickerOverrides={setHubPickerOverrides}
            setAddHubTarget={setAddHubTarget}
          />
        )}
      </div>

      {/* Footer */}
      <div className="relative z-10 py-6 px-4">
        <Copyright />
      </div>
    </div>
  );
}
